import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './FeaturePage.css';

const RevenueFeatures = () => {
  const { token } = useAuth();

  usePageMetadata({
    title: 'Revenue Dashboard | Ad Revenue Analytics & Optimization',
    description:
      'Maximize your ad revenue with detailed insights into ad performance, partner performance, and revenue optimization. Track RPM, CPM, viewability, and identify top-performing ad units.',
    canonicalUrl: 'https://theadshop.co/revenue-features',
    meta: [
      { property: 'og:title', content: 'Revenue Dashboard | Ad Revenue Analytics & Optimization' },
      {
        property: 'og:description',
        content:
          'Track ad revenue performance, optimize RPM and CPM, analyze ad unit and partner performance, and maximize your monetization with detailed revenue insights.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/revenue-features' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Revenue Dashboard | Ad Revenue Analytics & Optimization' },
      {
        name: 'twitter:description',
        content:
          'Maximize your ad revenue with detailed insights into ad performance, partner performance, and revenue optimization.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Revenue Dashboard Features | The Ad Shop',
        url: 'https://theadshop.co/revenue-features',
        description:
          'Learn about The Ad Shop Revenue Dashboard features including ad revenue tracking, RPM and CPM analysis, ad unit performance, partner comparison, viewability metrics, and revenue optimization.',
        isPartOf: {
          '@type': 'WebSite',
          name: 'The Ad Shop',
          url: 'https://theadshop.co/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://theadshop.co/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Revenue Dashboard',
              item: 'https://theadshop.co/revenue-features',
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Revenue Dashboard',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
        },
        featureList: [
          'Ad revenue tracking and RPM analysis',
          'Ad unit performance breakdown',
          'Ad partner performance comparison',
          'Viewability metrics and optimization',
          'Geographic revenue analysis',
          'Revenue trends and forecasting',
          'Advanced filtering by ad partner, unit, size, and geography',
        ],
        description:
          'Revenue dashboard that helps publishers maximize ad revenue through detailed performance insights, ad unit analysis, partner comparison, and viewability optimization.',
      },
    ],
  });

  return (
    <div className="feature-page">
      <div className="feature-container">
        <div className="feature-header">
          <div className="feature-icon-large">💰</div>
          <h1>Revenue Dashboard</h1>
          <p className="feature-subtitle">
            Maximize your ad revenue with detailed insights into ad performance, partner performance, and revenue optimization. 
            Track RPM, CPM, viewability, and identify top-performing ad units and partners.
          </p>
        </div>

        <div className="feature-content">
          <section className="feature-section">
            <h2>Comprehensive Revenue Tracking</h2>
            <p>
              Get a complete picture of your ad revenue performance with real-time data from Google AdSense and other ad partners:
            </p>
            <ul className="feature-list">
              <li><strong>Total Revenue:</strong> Track your overall earnings with period-over-period comparisons</li>
              <li><strong>RPM (Revenue Per Mille):</strong> Measure revenue per 1,000 pageviews to understand monetization efficiency</li>
              <li><strong>CPM (Cost Per Mille):</strong> Analyze cost per 1,000 impressions to optimize ad pricing</li>
              <li><strong>RPU (Revenue Per User):</strong> Understand how much revenue each user generates</li>
              <li><strong>Ad Impressions:</strong> Track total ad impressions and viewability rates</li>
              <li><strong>Viewability:</strong> Monitor ad viewability percentages to ensure ads are being seen</li>
            </ul>
            <p>
              All metrics include percentage change indicators so you can quickly see if performance is improving or declining.
            </p>
          </section>

          <section className="feature-section">
            <h2>Ad Unit Performance Analysis</h2>
            <p>
              Identify which ad units are driving the most revenue and optimize your ad placement strategy:
            </p>
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">📊</div>
                <h3>Top Ad Units</h3>
                <p>Ranked list of your best-performing ad units by revenue, RPM, and impressions</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📏</div>
                <h3>Ad Size Analysis</h3>
                <p>Compare performance across different ad sizes (300x250, 728x90, etc.) to find optimal formats</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🤖</div>
                <h3>Auto Ads Tracking</h3>
                <p>Monitor performance of auto ads including Anchor ads and Interstitial ads</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">👁️</div>
                <h3>Viewability Metrics</h3>
                <p>Track viewability rates for each ad unit to ensure maximum visibility</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <h3>Revenue Trends</h3>
                <p>Visualize revenue trends over time with interactive charts and daily breakdowns</p>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🌍</div>
                <h3>Geographic Performance</h3>
                <p>Analyze revenue by country and region to understand geographic monetization patterns</p>
              </div>
            </div>
          </section>

          <section className="feature-section">
            <h2>Ad Partner Comparison</h2>
            <p>
              Compare performance across different ad partners and networks to optimize your monetization strategy:
            </p>
            <ul className="feature-list">
              <li><strong>Partner Performance:</strong> See which ad partners (AdSense, AdX, Header Bidding, etc.) generate the most revenue</li>
              <li><strong>RPM Comparison:</strong> Compare RPM rates across partners to identify the best performers</li>
              <li><strong>CPM Analysis:</strong> Understand which partners offer the best CPM rates</li>
              <li><strong>Viewability by Partner:</strong> Track viewability metrics for each ad partner</li>
              <li><strong>Top Partners Table:</strong> Ranked list of ad partners by revenue performance</li>
            </ul>
            <p>
              Use partner comparison data to make informed decisions about which ad networks to prioritize and optimize.
            </p>
          </section>

          <section className="feature-section">
            <h2>Advanced Filtering & Segmentation</h2>
            <p>
              Drill down into specific revenue segments with powerful filtering options:
            </p>
            <ul className="feature-list">
              <li><strong>Ad Partner Filter:</strong> Filter data by specific ad partner (AdSense, AdX, Header Bidding, etc.)</li>
              <li><strong>Ad Unit Filter:</strong> Focus on specific ad units to analyze their individual performance</li>
              <li><strong>Ad Size Filter:</strong> Compare performance across different ad sizes</li>
              <li><strong>Geographic Filter:</strong> Analyze revenue performance by country or region</li>
              <li><strong>Auto Ad Type Filter:</strong> Filter by auto ad types (Anchor, Interstitial, etc.)</li>
              <li><strong>Date Range:</strong> Custom date ranges, 7-day, 14-day, 30-day, and custom period views</li>
            </ul>
            <p>
              When you filter by ad partner, all other filters automatically restrict to options available for that specific partner, 
              making it easy to analyze partner-specific performance.
            </p>
          </section>

          <section className="feature-section">
            <h2>Interactive Revenue Visualizations</h2>
            <p>
              Beautiful charts and visualizations make revenue data easy to understand:
            </p>
            <ul className="feature-list">
              <li><strong>Revenue Trend Chart:</strong> Interactive line chart showing revenue, RPM, CPM, and impressions over time</li>
              <li><strong>Daily Breakdown:</strong> Day-by-day revenue breakdown with comparison to previous periods</li>
              <li><strong>Top Ad Units Table:</strong> Sortable table with revenue, RPM, CPM, impressions, and viewability metrics</li>
              <li><strong>Top Partners Table:</strong> Ranked comparison of ad partner performance</li>
              <li><strong>Metric Cards:</strong> Key metrics displayed prominently with percentage change indicators</li>
            </ul>
          </section>

          <section className="feature-section">
            <h2>Viewability Optimization</h2>
            <p>
              Maximize ad visibility and revenue with viewability insights:
            </p>
            <ul className="feature-list">
              <li><strong>Overall Viewability:</strong> Track your site's average viewability rate</li>
              <li><strong>Unit-Level Viewability:</strong> See viewability rates for each individual ad unit</li>
              <li><strong>Partner Viewability:</strong> Compare viewability rates across different ad partners</li>
              <li><strong>Geographic Viewability:</strong> Understand viewability patterns by country</li>
              <li><strong>Optimization Recommendations:</strong> Identify low viewability units that need attention</li>
            </ul>
            <p>
              Higher viewability rates typically lead to better CPMs and increased revenue, so tracking and optimizing viewability 
              is crucial for maximizing monetization.
            </p>
          </section>

          <section className="feature-section">
            <h2>Easy Setup & Data Sync</h2>
            <p>
              Connect your AdSense account and start tracking revenue in minutes:
            </p>
            <ul className="feature-list">
              <li>One-click connection to Google AdSense via secure OAuth</li>
              <li>Automatic data synchronization from AdSense API</li>
              <li>Manual refresh option to pull latest data on demand</li>
              <li>Support for multiple ad partners and networks</li>
              <li>Historical data retention for trend analysis</li>
            </ul>
            <p>
              Once connected, your revenue data syncs automatically, giving you up-to-date insights into your ad monetization performance.
            </p>
          </section>

          <section className="feature-cta">
            <h2>Ready to Maximize Your Revenue?</h2>
            <p>
              Start tracking and optimizing your ad revenue performance today with detailed insights and analytics.
            </p>
            {token ? (
              <Link to="/revenue" className="btn-primary">
                Go to Revenue Dashboard
              </Link>
            ) : (
              <Link to="/signup" className="btn-primary">
                Sign Up Free
              </Link>
            )}
            <p className="cta-note">Free to get started • No credit card required • Setup in minutes</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RevenueFeatures;

